import { CredentialType } from '../shared';
import { FILE_PATH_FIELDS, type FilePathField } from './types';

export interface ValidationError {
  field: string;
  message: string;
}

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
}

/**
 * Validates that a file path is relative and doesn't contain path traversal.
 * File paths must:
 * - Not start with '/' (must be relative)
 * - Not contain '..' (no path traversal)
 * - Not be empty if required
 */
export const validateFilePath = (path: string, fieldName: string, required = false): ValidationError | null => {
  if (!path || path.trim() === '') {
    if (required) {
      return { field: fieldName, message: `${fieldName} is required` };
    }

    return null;
  }

  const trimmedPath = path.trim();

  if (trimmedPath.startsWith('/')) {
    return {
      field: fieldName,
      message: 'Path must be relative (cannot start with /). Use a namespaced path like "my-entity/filename".',
    };
  }

  if (trimmedPath.includes('..')) {
    return {
      field: fieldName,
      message: 'Path cannot contain ".." (path traversal not allowed)',
    };
  }

  return null;
};

/**
 * Checks if a field key is a file path field.
 */
export const isFilePathField = (key: string): key is FilePathField => {
  return FILE_PATH_FIELDS.includes(key as FilePathField);
};

/**
 * Validates SFTP credential values.
 * Rules:
 * - Must have (username + password) OR keyFilePath
 * - knownHostsFile is always required
 */
export const validateSftpCredential = (values: Record<string, string>): ValidationResult => {
  const errors: ValidationError[] = [];

  const hasUsername = Boolean(values.username?.trim());
  const hasPassword = Boolean(values.password?.trim());
  const hasKeyFilePath = Boolean(values.keyFilePath?.trim());
  const hasKnownHostsFile = Boolean(values.knownHostsFile?.trim());

  if (!hasKnownHostsFile) {
    errors.push({ field: 'knownHostsFile', message: 'Known Hosts Path is required for SFTP connections' });
  } else {
    const pathError = validateFilePath(values.knownHostsFile, 'knownHostsFile', true);
    if (pathError) errors.push(pathError);
  }

  const hasPasswordAuth = hasUsername && hasPassword;
  const hasKeyAuth = hasKeyFilePath;

  if (!hasPasswordAuth && !hasKeyAuth) {
    errors.push({
      field: 'credential',
      message: 'Either (Username + Password) or Private Key Path is required',
    });
  }

  if (hasKeyFilePath) {
    const pathError = validateFilePath(values.keyFilePath, 'keyFilePath');
    if (pathError) errors.push(pathError);
  }

  return { isValid: errors.length === 0, errors };
};

/**
 * Validates AWS credential values.
 * Rules:
 * - Must have (accessKeyId + secretAccessKey) OR roleArn
 */
export const validateAwsCredential = (values: Record<string, string>): ValidationResult => {
  const errors: ValidationError[] = [];

  const hasAccessKeyId = Boolean(values.accessKeyId?.trim());
  const hasSecretAccessKey = Boolean(values.secretAccessKey?.trim());
  const hasRoleArn = Boolean(values.roleArn?.trim());

  const hasAccessKeyAuth = hasAccessKeyId && hasSecretAccessKey;

  if (!hasAccessKeyAuth && !hasRoleArn) {
    errors.push({
      field: 'credential',
      message: 'Either (Access Key ID + Secret Access Key) or Role ARN is required',
    });
  }

  if (hasAccessKeyId && !hasSecretAccessKey) {
    errors.push({ field: 'secretAccessKey', message: 'Secret Access Key is required when Access Key ID is provided' });
  }

  if (hasSecretAccessKey && !hasAccessKeyId) {
    errors.push({ field: 'accessKeyId', message: 'Access Key ID is required when Secret Access Key is provided' });
  }

  return { isValid: errors.length === 0, errors };
};

/**
 * Validates GCP credential values.
 * Rules:
 * - credentialFilePath is required
 */
export const validateGcpCredential = (values: Record<string, string>): ValidationResult => {
  const errors: ValidationError[] = [];

  if (!values.credentialFilePath?.trim()) {
    errors.push({ field: 'credentialFilePath', message: 'Service Account JSON Path is required' });
  } else {
    const pathError = validateFilePath(values.credentialFilePath, 'credentialFilePath', true);
    if (pathError) errors.push(pathError);
  }

  return { isValid: errors.length === 0, errors };
};

/**
 * Validates Azure credential values.
 * Rules:
 * - Must have (accountName + accountKey) OR (tenantId + clientId + clientSecret)
 */
export const validateAzureCredential = (values: Record<string, string>): ValidationResult => {
  const errors: ValidationError[] = [];

  const hasAccountName = Boolean(values.accountName?.trim());
  const hasAccountKey = Boolean(values.accountKey?.trim());
  const hasTenantId = Boolean(values.tenantId?.trim());
  const hasClientId = Boolean(values.clientId?.trim());
  const hasClientSecret = Boolean(values.clientSecret?.trim());

  const hasAccountAuth = hasAccountName && hasAccountKey;
  const hasServicePrincipalAuth = hasTenantId && hasClientId && hasClientSecret;

  if (!hasAccountAuth && !hasServicePrincipalAuth) {
    errors.push({
      field: 'credential',
      message: 'Either (Account Name + Account Key) or (Tenant ID + Client ID + Client Secret) is required',
    });
  }

  return { isValid: errors.length === 0, errors };
};

/**
 * Validates credential values based on credential type.
 */
export const validateCredentialValues = (type: CredentialType, values: Record<string, string>): ValidationResult => {
  switch (type) {
    case CredentialType.CREDENTIAL_TYPE_SSH:
      return validateSftpCredential(values);
    case CredentialType.CREDENTIAL_TYPE_AWS:
      return validateAwsCredential(values);
    case CredentialType.CREDENTIAL_TYPE_GCP_SERVICE_ACCOUNT:
      return validateGcpCredential(values);
    case CredentialType.CREDENTIAL_TYPE_AZURE_SERVICE_PRINCIPAL:
      return validateAzureCredential(values);
    default:
      return { isValid: true, errors: [] };
  }
};

/**
 * Constructs a suggested file path from service account username and filename.
 * Returns a path with leading slash (intentionally invalid) to force user correction.
 */
export const suggestFilePath = (filename: string): string => {
  return `/${filename}`;
};

/**
 * Constructs a proper file path from namespace and filename.
 */
export const constructFilePath = (namespace: string, filename: string): string => {
  return `${namespace}/${filename}`;
};
